import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { ServiceGroup, QuoteDetail, Vehicle, VehicleTemplateDetail } from '@quorum/models/xs-resource';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { DeepCopyPipe } from '@quorum/common-pipes';
import { FadeInOut } from '@quorum/sha-animations';
import { FixedOperationsStateService } from '@quorum/xsr-fixed-operations/services';
import { LabourOperationsStateService } from '@quorum/xsr-labour-operations/services';
import { VehicleMaintenanceStateService } from '@quorum/xsr-vehicle-maintenance/services';
import { VehicleStateService } from '@quorum/xsr-vehicle/services';
import { BehaviorSubject } from 'rxjs';
import { Observable } from 'rxjs';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { servicesDueWithMenuData } from '../menu/services-due-with-menu-data';
import { SalesHintDialogComponent } from '../sales-hint-dialog/sales-hint-dialog.component';
// eslint-disable-next-line max-len
import { SelectDeclinedReasonDialogComponent } from '../select-declined-reason-dialog/select-declined-reason-dialog.component';
// eslint-disable-next-line max-len
import { ServiceSelectionDialogComponent } from '../service-selection-dialog/service-selection-dialog.component';
import { TwoActionDialogComponent, TwoActionDialogData } from '@quorum/sha-components-ng-ui';

import { MatIconRegistry } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'xsr-quote-menu',
  templateUrl: './quote-menu.component.html',
  styleUrls: ['./quote-menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    FadeInOut,
    trigger('flyOutIn', [
      state(
        'shrink',
        style({
          display: 'none',
          flex: 0,
          transform: 'translateX(-160%)',
        })
      ),
      state(
        'full',
        style({
          display: 'flex',
        })
      ),
      transition('full => shrink', animate('138ms ease-in')),
      transition('shrink => full', animate('138ms ease-out')),
    ]),
    trigger('flyInOut', [
      state(
        'full',
        style({
          'box-sizing': 'border-box',
          flex: '1 1 100%',
          display: 'flex',
          width: '28%',
          'max-width': '28%',
        })
      ),
      state(
        'shrink',
        style({
          'box-sizing': 'border-box',
          flex: '1 1 100%',
          display: 'flex',
          width: '100%',
          'max-width': '100%',
        })
      ),
      transition('full => shrink', animate('138ms ease-in')),
      transition('shrink => full', animate('138ms ease-out')),
    ]),
  ],
})
export class QuoteMenuComponent implements OnInit {
  $menuData: Observable<any>;
  $menuData2: Observable<any>;
  $labourOpData: Observable<any>;
  $factoryRequiredServices: Observable<any>;
  $factoryRecommendedServices: Observable<any>;
  $dealerRecommendedServices: Observable<any>;
  $vehicle: Observable<any>;
  roHistory: any = [];
  savedAccepted: any = [];
  savedDeclined: any = [];
  pendingAccepted: any = [];
  pendingDeclined: any = [];
  dank: string = 'full';
  requestingPending: string = '';
  declinedReasons: any = [];
  declinedServiceReasons: any = [];
  vehicle: Vehicle;
  dueOnlySubject: BehaviorSubject<boolean>;
  factoryRequiredServices: any;
  factoryRecommendedServices: any;
  dealerRecommendedServices: any;
  $workerObservable: Observable<any>;
  authenticatedUser$: Observable<any>;
  saving: boolean = false;
  saveProgress: number = 0;
  constructor(
    private activatedRoute: ActivatedRoute,
    private authenticationStateService: AuthenticationStateService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private sgService: LabourOperationsStateService,
    private vehicleService: VehicleStateService,
    private vmService: VehicleMaintenanceStateService,
    private changeDetector: ChangeDetectorRef,
    public dialog: MatDialog,
    private fixedOperationsService: FixedOperationsStateService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit() {
    this.fixedOperationsService.listenForToasts().subscribe((toastOptions) => {
      this.snackBar.open(toastOptions.message, toastOptions.actionText, toastOptions.options);
    });
    this.authenticatedUser$ = this.authenticationStateService.selectAuthenticatedUser();
    this.dueOnlySubject = new BehaviorSubject<boolean>(true);
    this.iconRegistry.addSvgIcon(
      'service-drive',
      this.sanitizer.bypassSecurityTrustResourceUrl('assets/service-drive.svg')
    );

    this.$labourOpData = combineLatest(
      this.sgService.getServiceGroups(),
      this.sgService.getServiceGroupRates(),
      this.sgService.getServices()
    ).pipe(
      map(([serviceGroups, serviceGroupRates, services]) => {
        return {
          serviceGroups: this.sgService.isEmpty(services) ? null : new DeepCopyPipe().transform(serviceGroups),
          serviceGroupRates: this.sgService.isEmpty(services) ? null : new DeepCopyPipe().transform(serviceGroupRates),
          services: this.sgService.isEmpty(services) ? null : new DeepCopyPipe().transform(services),
        };
      })
    );

    this.sgService.selectDeclinedReasons().subscribe((declinedReasons: any) => {
      this.declinedReasons = declinedReasons;
    });

    //needs to be shithauled to improve performance
    this.$vehicle = this.vehicleService.getVehicle(this.activatedRoute.snapshot.params.id);

    this.$vehicle.subscribe((vehicle) => (this.vehicle = vehicle));

    this.$menuData2 = combineLatest(
      this.vmService.getServicesDue().pipe(
        map((servicesDue) => {
          return new DeepCopyPipe().transform(servicesDue);
        })
      ),
      this.sgService.getServiceGroups().pipe(
        filter((serviceGroups) => !this.sgService.isEmpty(serviceGroups)),
        map((serviceGroups) => {
          return new DeepCopyPipe().transform(serviceGroups);
        })
      ),
      this.vmService.getTemplateDetail().pipe(
        map((templateDetail) => {
          return new DeepCopyPipe().transform(templateDetail);
        })
      ),
      this.vehicleService.getVehicle(this.activatedRoute.snapshot.params.id)
    ).pipe(
      map(([allServicesDue, serviceGroups, templateDetail, vehicle]) => {
        const servicesDue = servicesDueWithMenuData(allServicesDue, serviceGroups, templateDetail);
        servicesDue.forEach((serviceDue: any) => {
          serviceDue.vehicleMileage = vehicle.currentMileage;
          serviceDue.serviceGroup = new DeepCopyPipe().transform(serviceGroups[serviceDue.serviceGroupId]);
          serviceDue.displayServiceGroup = new DeepCopyPipe().transform(serviceGroups[serviceDue.serviceGroupId]);
          serviceDue.frequencyMileage = templateDetail.find(
            (templateDetail: VehicleTemplateDetail) => templateDetail.serviceGroupId === serviceDue.serviceGroupId
          ).frequencyMileage;
          serviceDue.frequencyMonths = templateDetail.find(
            (templateDetail: VehicleTemplateDetail) => templateDetail.serviceGroupId === serviceDue.serviceGroupId
          ).frequencyMonths;
          let date = new Date();
          date.setDate(new Date().getDate() + 21);
          if (
            serviceDue.overdue ||
            new Date(serviceDue.date).getTime() < date.getTime() ||
            vehicle.currentMileage > serviceDue.mileage
          ) {
            serviceDue.dueDate = new Date();
            if (vehicle.currentMileage > serviceDue.mileage) {
              serviceDue.dueBy = ' by Mileage';
            } else {
              serviceDue.dueBy = ' by Date';
            }
          } else {
            serviceDue.dueDate = new Date(serviceDue.date);
            serviceDue.dueBy = ' by Date';
          }

          serviceDue.serviceTypeId = templateDetail.find(
            (templateDetail: VehicleTemplateDetail) => templateDetail.serviceGroupId === serviceDue.serviceGroupId
          ).serviceTypeId;
          if (serviceDue.serviceGroup.useFlag === 'M') {
            for (var i = 0; i < serviceDue.serviceGroup.mappedServiceGroups.length; i++) {
              serviceDue.serviceGroup.mappedServiceGroups[i] =
                serviceGroups[serviceDue.serviceGroup.mappedServiceGroups[i]];
            }

            serviceDue.serviceGroup.mappedServiceGroups = serviceDue.serviceGroup.mappedServiceGroups.filter(
              (msg: any) => {
                return !!msg && (msg.expiryDate == null || new Date(msg.expiryDate) > new Date());
              }
            );
          }
        });

        return servicesDue;
      })
    );

    //search thru due services and see if any already been accepted/declined on current transaction
    function sameDay(d1: Date, d2: Date) {
      return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
    }

    let determineServices = map(([servicesDue, onlyDue]) => {
      let filteredServices = servicesDue.filter((serviceDue: any) => {
        if ((onlyDue && sameDay(serviceDue.dueDate, new Date())) || !onlyDue) {
          return serviceDue;
        }
      });
      return filteredServices;
    });

    let getServicesOfType = function (typeId: number) {
      return map((servicesDue: any) => {
        let returnVal = servicesDue.filter((serviceDue: any) => {
          return serviceDue.serviceTypeId === typeId;
        });
        return returnVal;
      });
    };

    this.$factoryRequiredServices = combineLatest(this.$menuData2.pipe(getServicesOfType(1)), this.dueOnlySubject).pipe(
      determineServices
    );

    this.$factoryRequiredServices.subscribe((factoryRequiredServices) => {
      this.factoryRequiredServices = factoryRequiredServices;
      this.changeDetector.detectChanges();
    });

    this.$factoryRecommendedServices = combineLatest(
      this.$menuData2.pipe(getServicesOfType(2)),
      this.dueOnlySubject
    ).pipe(determineServices);

    this.$factoryRecommendedServices.subscribe((factoryRecommendedServices) => {
      this.factoryRecommendedServices = factoryRecommendedServices;
      this.changeDetector.detectChanges();
    });

    this.$dealerRecommendedServices = combineLatest(
      this.$menuData2.pipe(getServicesOfType(3)),
      this.dueOnlySubject
    ).pipe(determineServices);

    this.$dealerRecommendedServices.subscribe((dealerRecommendedServices) => {
      this.dealerRecommendedServices = dealerRecommendedServices;
      this.changeDetector.detectChanges();
    });
  }

  acceptService(serviceGroup: any, labourOpData: any, employeeId: string) {
    this.changeDetector.detectChanges();
    const dialogData: TwoActionDialogData = {
      actionTwoCaption: 'Add Service',
      actionOneCaption: 'Cancel',
      content: 'This service will be added to the associated quote.',
      title: 'Add Service?',
    };

    const dialogRef = this.dialog.open(TwoActionDialogComponent, {
      width: '400px',
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      let quoteDetail: QuoteDetail = new QuoteDetail();
      quoteDetail.rooftopId = serviceGroup.rooftopId;
      quoteDetail.employeeId = employeeId;
      quoteDetail.associateId = employeeId;
      quoteDetail.quoteHeaderId = this.activatedRoute.snapshot.params.quoteId;
      quoteDetail.serviceGroupId = serviceGroup.id;
      quoteDetail.serviceName;
      quoteDetail.serviceName = serviceGroup.name + ' (';
      serviceGroup.services.forEach((service: any) => {
        quoteDetail.serviceName += ' ' + labourOpData.services[service].name;
      });
      quoteDetail.serviceName += ')';
      quoteDetail.opCode = serviceGroup.workOrderLocationOpCode;
      quoteDetail.recommendedHours = serviceGroup.serviceGroupRateId
        ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].recommendedHours != null
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].recommendedHours
          : serviceGroup.recommendedHours
        : serviceGroup.recommendedHours;

      quoteDetail.technicianGroupId = serviceGroup.technicianGroupId;
      quoteDetail.technicianGroupName = serviceGroup.technicianGroupName;
      quoteDetail.technicianId = serviceGroup.technicianId;
      quoteDetail.warrantySubmissionTypeId =
        serviceGroup.warrantySubmissionTypeId != null && serviceGroup.warrantySubmissionTypeId.trim() != ''
          ? serviceGroup.warrantySubmissionTypeId
          : 'CP';

      quoteDetail.rateType = labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId]
        ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rateType
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rateType
          : null
        : null;

      quoteDetail.rate = labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId]
        ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rate
          ? labourOpData.serviceGroupRates[serviceGroup.serviceGroupRateId].rate
          : null
        : null;

      quoteDetail.lineAddedFromServiceSchedule = 1;
      quoteDetail.expirationDate = new Date();
      quoteDetail.expirationDate.setMonth(quoteDetail.expirationDate.getMonth() + 1);
      this.fixedOperationsService.createQuoteCase(quoteDetail);
      //if (result) this.membersStateService.removeSelfFromConversation(this.member);
    });
  }

  sliderChanged(event: any) {
    this.dueOnlySubject.next(event.checked);
  }
  toggleAnimation() {
    this.dank = this.dank === 'full' ? 'shrink' : 'full';
  }

  declineService(serviceGroup: any) {
    //move this logic to disable accept?
    //if (serviceGroup.rate && serviceGroup.rate.rate) {
    this.vmService.addPendingDeclinedService(serviceGroup);
    //this.savedDeclined.push(serviceGroup);
    //}
  }

  selectService(menuItem: any, serviceGroups: any, serviceGroupRates: any) {
    let dialogRef = this.dialog.open(ServiceSelectionDialogComponent, {
      width: '400px',
      data: { mappedServiceGroups: menuItem.serviceGroup.mappedServiceGroups, serviceGroups, serviceGroupRates },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // A backdrop click or Esc closes the dialog with no result.
      if (result && !result.cancelled) {
        menuItem.displayServiceGroup = serviceGroups[result.selectedServiceGroup];
        this.changeDetector.detectChanges();
      }
    });
  }

  selectDeclinedReason(menuItem: any, declinedReasons: any[]) {
    let dialogRef = this.dialog.open(SelectDeclinedReasonDialogComponent, {
      width: '400px',
      data: { declinedReasons: declinedReasons },
    });

    dialogRef.afterClosed().subscribe((result) => {
      // A backdrop click or Esc closes the dialog with no result.
      if (result && !result.cancelled) {
        this.declinedServiceReasons.push({
          serviceGroupId: menuItem.displayServiceGroup.id,
          declinedReason: result.selectedDeclinedReason,
        });
        this.declineService(menuItem.displayServiceGroup);
        this.changeDetector.detectChanges();
      }
    });
  }

  getStyle(serviceGroup: any): object {
    let style = {};

    if (this.savedAccepted.find((e: any) => e.id === serviceGroup.id)) {
      style = { color: '#2E7D32' };
    } else if (this.savedDeclined.find((e: any) => e.id === serviceGroup.id)) {
      style = { color: '#C62828' };
    }

    return style;
  }

  getTotal(values: any[], rates: any) {
    if (values.length > 0 && rates) {
      return values.reduce(function (prev: any, cur: any) {
        return cur.serviceGroupRateId
          ? prev + (rates[cur.serviceGroupRateId].rate ? rates[cur.serviceGroupRateId].rate : 0)
          : prev;
        //return prev + (rates[cur.serviceGroupRateId].rate ? rates[cur.serviceGroupRateId].rate : 0);
      }, 0);
    } else {
      return 0;
    }
  }

  getPrice(serviceGroup: any, serviceGroupRates: any) {
    if (serviceGroupRates && serviceGroupRates[serviceGroup.serviceGroupRateId] != null) {
      return serviceGroupRates[serviceGroup.serviceGroupRateId].rate;
    } else {
      return 0;
    }
  }

  viewSalesHint(salesHint: string) {
    let dialogRef = this.dialog.open(SalesHintDialogComponent, {
      width: '400px',
      data: { salesHint: salesHint },
    });

    dialogRef.afterClosed().subscribe((result) => {});
  }
  getDeclinedReason(declinedService: ServiceGroup) {
    let declinedReason = this.declinedServiceReasons.find((reason: any) => {
      return (reason.serviceGroupId = declinedService.id);
    });
    if (declinedReason) {
      return ` (${declinedReason.declinedReason.reason})`;
    }
  }
}
